/**
 * Releasenoter för Centralspec. Nyaste överst. `version` används både som
 * visningsetikett och som nyckel i localStorage för auto-open vid version-bump.
 */
export type ReleaseNote = {
  version: string;
  date: string;
  title?: string;
  items: string[];
};

export const RELEASES: ReleaseNote[] = [
  {
    version: "0.7.0",
    date: "2026-04-19",
    title: "Flera laster per rad + auto-säkring + live-enlinje till vänster",
    items: [
      "Ny 'Laster'-kolumn: på samma rad kan du lägga flera objekt (ljuspunkter, uttag, motorer, värmare, spis, EV-laddare, varmvattenberedare, annat) med antal och W/styck.",
      "Auto-säkring (AUTO-badge): när laster är angivna summeras total effekt, ampere räknas ut (1-fas 230 V eller 3-fas 400 V med cosφ per lasttyp) och närmaste standard-säkring väljs med 25 % marginal från 6/10/13/16/20/25/32/40/50/63/80/100 A.",
      "Karakteristik C föreslås automatiskt när motor eller EV-laddare finns på raden (hög startström), annars B.",
      "Popover öppnas vid klick på laster-cellen: lägg till, ändra eller ta bort laster; slå på/av auto-säkring per rad; se total last och förslaget säkring live.",
      "Split-view: enlinjeschemat visas nu alltid till vänster (~35 %) när du redigerar tabellen. Dra i den vertikala avdelaren för att ändra delningen — storleken sparas i webbläsaren.",
      "Enlinje-fliken kvar för fullskärms-vy när du vill titta nära på schemat eller visa på skärm.",
      "Standard-effekter per lasttyp: ljuspunkt 18 W, uttag 2300 W (1-fas 10 A), motor 1100 W, spis 8000 W — kan skrivas över per last.",
    ],
  },
  {
    version: "0.6.0",
    date: "2026-04-19",
    title: "Symbolpalett, revisioner, granskning och fildelning",
    items: [
      "Ny symbolpalett (▦ i toolbaren): klicka för att infoga någon av 33 vanliga centralsymboler — dvärgbrytare, JFB, RCBO, effektbrytare, säkringslastbrytare, överspänningsskydd, kontaktor, hjälprelä, impulsrelä, mätare, transformator, plintar (PUS, N, sektion) och mer.",
      "Alla symboler är egna ritningar i SS-EN 60617-stil. När en rad har en känd kortkod visas ikonen också i DIN-modul-layouten.",
      "Nya katalogkategorier: SPD (typ 1 / 2 / 1+2), Säkringslastbrytare (SLF), PUS-plint, N-plint, sektioneringsplint, transformator (T), KWHS, impulsrelä, kontaktor 20/25/40 A.",
      "Granskad-kolumn: bocka av rader som är färdiggranskade. Bocken följer med i PDF- och Excel-export, och duplicerade rader återställs som ogranskade.",
      "Projektinformation-dialog (Info-knappen): konstruktör, företag, telefon, kapsling, plats och fria anteckningar. Syns i titelblocket i PDF-utskrift.",
      "Revisionshistorik (Rev.-knappen): skapa tidpunkter (Rev A, Rev B …) för nuvarande state. Varje revision kan laddas ner som fristående JSON.",
      "Servis-meny i toolbaren: välj jordningssystem (TN-S / TN-C / TN-C-S / TT / IT) — syns i PDF-header.",
      "Status-badge (Utkast / Granskning / Klar) med ett klick växlar mellan lägena. Klar-status visas som grön chip bredvid titeln i PDF:en.",
      "Importera .panelspec-fil från dropdown-menyn: skapa ny central utifrån exporten från en annan dator/användare. Magic + version valideras vid import.",
      "Kabelnr-kolumn: separat fält för kabelmärkning (t.ex. K101) — tidigare fick det klämmas in i noteringen.",
      "Tab/Shift+Tab i Bet.-cellen ökar/minskar indent-nivå (0-3) för att visa hierarki (underliggande rader till en huvudbrytare).",
      "Live-enlinjeschema (ny 'Enlinje'-flik): samma SVG som PDF-sidan 2, men renderad i webbläsaren så du kan förhandsvisa innan utskrift.",
      "Tangentbordsgenvägar: Ctrl+I lägger till en rad efter, Ctrl+5 lägger till fem, Ctrl+D duplicerar rad, Ctrl+E tar bort rad, Ctrl+Shift+C kopierar värden från raden ovan. '?' öppnar förkortnings-dialogen.",
    ],
  },
  {
    version: "0.5.0",
    date: "2026-04-19",
    title: "DIN-modul-layout + import från schemaeditorn",
    items: [
      "Ny vy 'Layout' i toolbaren: visualisera DIN-skenornas modul-placering. Varje komponent tar sin TE-bredd (1-8 moduler beroende på typ) och färgas efter kategori.",
      "Justera antal skenor och TE-moduler per skena direkt i layout-vyn. Oplacerade rader syns i en tray längst ner och kan tilldelas en skena via dra-och-släpp eller rail-knappar.",
      "Overflow-varning när skenans kapacitet överskrids — bakgrunden blir röd och moduler sticker ut till höger så problemet är uppenbart.",
      "PDF-exporten får en tredje sida: central-layouten ritas som SVG med TE-grid och färgkodade komponent-block. Inkluderar oplacerade rader som chip.",
      "Koppla centralen till ett schemaeditor-projekt: knapp 'Koppla till projekt' i toolbaren öppnar import-dialog. Klistra in JSON eller ladda upp fil från schemaeditorns 'Centralspec'-knapp.",
      "När en koppling finns: 'Användning'-kolumnen föreslår automatiskt etiketter/beteckningar från schemaeditor-projektets apparater. Skriver du i cellen filtreras förslagen på prefix.",
      "Kopplingen är per centralspec-dokument (lagras i localStorage). Importen är ensidig — uppdatera genom att exportera på nytt från schemaeditorn.",
    ],
  },
  {
    version: "0.4.0",
    date: "2026-04-19",
    title: "Enlinjeschema auto-genereras från tabellen",
    items: [
      "PDF-exporten får en andra sida: ett auto-genererat enlinjeschema (SLD) baserat på tabellens rader.",
      "Huvudbrytaren ligger längst upp, varje sektion får egen skena med säkringar/JFB/JFA/lastbrytare hängande under.",
      "Varje gren visar beteckning, komponenttyp, märkström, poler, karakteristik, kabel och användning — färgkodat per komponentkategori (MCB blått, RCD gult, RCBO grönt, MCCB rött).",
      "Sektioner med många rader wrappar automatiskt till flera skena-rader så schemat passar A4 stående.",
      "Ingen separat editor — schemat ritas om varje gång du klickar 'Exportera PDF'. Ändra bara tabellen, schemat följer med.",
    ],
  },
  {
    version: "0.3.0",
    date: "2026-04-19",
    title: "Mallbibliotek — kom igång på 10 sekunder",
    items: [
      "'+ Ny central…' öppnar ett mall-galleri. Välj 'Tom central' som förut, eller en av fyra grundmallar.",
      "Mall: Lägenhet 1 rum + kök — 3 sektioner, typiska kretsar för uttag, belysning, spis och tvätt.",
      "Mall: Villa 150 m² — huvudcentral med belysning, uttag, fast installation och EV-laddbox. 125 A skena.",
      "Mall: Industri lågspänning — grupp-central för verkstad, inklusive motorkretsar med C-karakteristik.",
      "Mall: Solceller (mikroinverter) — AC-central med strängar och mätning.",
      "Mallarna är bara utgångspunkter: byt beteckning, lägg till rader, flytta mellan sektioner — allt är redigerbart efteråt.",
    ],
  },
  {
    version: "0.2.0",
    date: "2026-04-19",
    title: "Sektionering, lastsummering och skena-varning",
    items: [
      "Sektionera panelen med S-koder (S1, S2 …). Varje sektion blir en samlad rubrik i tabellen med egen 'Lägg till rad'-knapp.",
      "Byt sektion på en rad via dropdown i sista kolumnen. Radera sektion och raderna blir osektionerade (inga förlorade data).",
      "Huvudskenans märkström kan anges i toolbaren. Statusraden längst ner visar total last (W och A) samt % av skena-kapacitet.",
      "Varning när panelens totala ström överstiger skena-kapaciteten — baseras på 'Last (W)'-kolumnen och antal poler (1-fas 230 V / 3-fas 400 V).",
      "Duplicerade beteckningar markeras nu tydligt med röd ram — inte bara amber-bakgrund som innan.",
      "Hovra över markerade celler för tooltip med varningstext och SS-referens.",
    ],
  },
  {
    version: "0.1.0",
    date: "2026-04-19",
    title: "Första version — tabellredigeraren lever",
    items: [
      "Centralspec är en ny fristående produkt för att snabbt skapa centralspecifikationer för beställning av ställverk och elcentraler.",
      "Excel-liknande tabell: Tab/Enter/piltangenter navigerar, Enter på sista raden skapar en ny rad.",
      "Kortkoder fyller i standardvärden: skriv 'DB' för dvärgbrytare, 'JFB' för jordfelsbrytare, 'JFA' för jordfelsautomat, 'LB' för lastbrytare. Kabel skrivs som '5G2,5'.",
      "Validering enligt SS 436 40 00 / IEC 60364: varnar när kabelarean är för liten för säkringsströmmen och när badrum/ute-uttag saknar jordfelsbrytare.",
      "Allt sparas automatiskt i webbläsaren via Yjs + IndexedDB — ingen inloggning krävs i lokalt läge. Flera centraler i olika flikar via /local/:id.",
      "Export till PDF (webbläsarens utskrift med A4-layout) och Excel (.xlsx).",
    ],
  },
];

export const CURRENT_VERSION = RELEASES[0]?.version ?? "0.1.0";
